import { clsx, ClassValue } from "clsx";
import {
    IClient,
    IClientBooking,
    ICancelClientSessionParams,
    ISessionCancellationChanges,
    ClientStatus,
    IIntakeSessionHistory,
    RecurrenceRule,
} from "intake_shared/src/types";
import { cloneClient } from "../utils/utils";

export function cn(...args: ClassValue[]) {
    return clsx(args);
}

export const hexToRGBA = (hex: string, alpha = 1): string => {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((c) => c + c)
            .join("");
    }

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}

export const convertArrayToString = (array: string[], separator?: string) => {
    return array.join(separator || " ");
};

/** REMOVES UNDERSCORE FROM ENUM VALUES */
export const removeEnumUnderscore = (value: string) => {
    return value?.replace(/_/g, " ");
};

// Complete client flow helpers
function buildAppointmentHistory(
    client: IClient,
    selectedAppointment: IClientBooking,
    loggedInUserEmail: string | null | undefined,
    sessionFields: {
        termination_message_requested: boolean;
        reason_for_termination_msg_skip: string;
    }
) {
    const cancellationNotes = client.cancellation_notes || {};
    const updateFields = {
        booking: selectedAppointment,
        reason: client.complete_notes.reason || "",
        notes: client.complete_notes.notes || "",
    };

    if (client.complete_notes.reason === "Termination") {
        updateFields["termination_message_requested"] =
            sessionFields.termination_message_requested;
        updateFields["reason_for_termination_msg_skip"] =
            sessionFields.reason_for_termination_msg_skip;
    }

    cancellationNotes[selectedAppointment.id] = {
        ...updateFields,
    };
    return {
        cancellation_notes: cancellationNotes,
        appointment_history: {
            ...selectedAppointment,
            cancelled_on: new Date().toISOString(),
            cancellation_notes:
                client.complete_notes.notes || client.complete_notes.reason || "",
            cancelled_by: loggedInUserEmail || "Clinician",
        },
    };
}

function getAppointmentsToCancel({
    client,
    selectedAppointmentId,
    caller,
    cancellerId,
}: ICancelClientSessionParams): IClientBooking[] {
    const appointmentsToCancel: IClientBooking[] = [];
    if (selectedAppointmentId === "ALL_APPOINTMENTS") {
        if (caller === "clinician") {
            appointmentsToCancel.push(
                ...client.confirmed_bookings.filter(
                    (c) => c.clinician_id === cancellerId
                )
            );
        } else if (caller === "admin") {
            appointmentsToCancel.push(...client.confirmed_bookings);
        }
    } else {
        // Single appointments.
        const selectedAppointment = client.confirmed_bookings.find(
            (c) => c.id === selectedAppointmentId
        );
        if (!selectedAppointment) {
            return appointmentsToCancel;
        }
        if (caller === "clinician") {
            if (selectedAppointment.clinician_id === cancellerId) {
                appointmentsToCancel.push(selectedAppointment);
            }
        } else if (caller === "admin") {
            appointmentsToCancel.push(selectedAppointment);
        }
    }
    return appointmentsToCancel;
}

export function buildSessionCancelChanges(
    cancelParams: ICancelClientSessionParams
): ISessionCancellationChanges {
    const updatedClient = cloneClient(cancelParams.client);
    const updatedClientChangesToSave = {
        status: updatedClient.status,
        appointment_history: updatedClient.appointment_history,
        cancellation_notes: updatedClient.cancellation_notes,
        confirmed_bookings: updatedClient.confirmed_bookings,
        complete_notes: updatedClient.complete_notes,
        intake_session_history: updatedClient.intake_session_history || [],
    };
    let updatedClininicianChangesToSave: ISessionCancellationChanges["updatedClininicianChangesToSave"] =
        {};
    const supervisorIntakeSessionsToCancel: Array<{
        supervisorId: string;
        bookingId: string;
        clientId: string;
    }> = [];
    const appointmentsToCancel = getAppointmentsToCancel({
        ...cancelParams,
        client: updatedClient,
    });

    for (const clinician of cancelParams.clinicians) {
        updatedClininicianChangesToSave[clinician.id!] = {
            confirmed:
                cancelParams.clinicians.find((c) => c.id === clinician.id)
                    ?.confirmed || [],
        };
    }
    if (appointmentsToCancel.length < 1 && cancelParams.caller !== "admin") {
        return {
            canCancel: false,
            numberOfCancellations: 0,
            message: "No confirmed appointments to cancel",
        };
    } else if (
        appointmentsToCancel.length +
            updatedClient.confirmed_bookings.length +
            updatedClient.held_bookings.length <
            1 &&
        cancelParams.caller === "admin" &&
        updatedClient.status === ClientStatus.APPT_CONFIRMED
    ) {
        updatedClientChangesToSave.status = ClientStatus.COMPLETED;
        return {
            canCancel: true,
            numberOfCancellations: 0,
            updatedClientChangesToSave,
        };
    }

    if (
        appointmentsToCancel.length ===
        cancelParams.client.confirmed_bookings.length +
            cancelParams.client.held_bookings.length
    ) {
        // Appointments will be cancelled.
        if (updatedClient.status !== ClientStatus.APPT_CONFIRMED) {
            updatedClientChangesToSave.status = updatedClient.status;
        } else {
            updatedClientChangesToSave.status = ClientStatus.COMPLETED;
        }
    }

    for (const appt of appointmentsToCancel) {
        appt.is_paused = false;
        const { cancellation_notes, appointment_history } = buildAppointmentHistory(
            updatedClient,
            appt,
            cancelParams.cancellerId,
            cancelParams.sessionFields
        );
        updatedClientChangesToSave.appointment_history.push(appointment_history);
        updatedClientChangesToSave.cancellation_notes = {
            ...updatedClientChangesToSave.cancellation_notes,
            ...cancellation_notes,
        };

        // Handle supervisor intake session cancellation if present
        if (appt.supervisor_intake_session) {
            const intakeSession = appt.supervisor_intake_session;
            const intakeSessionHistoryEntry: IIntakeSessionHistory = {
                id: intakeSession.id,
                booking_id: appt.id,
                supervisor_id: intakeSession.supervisor_id,
                start_time: intakeSession.start_time,
                end_time: intakeSession.end_time,
                status: intakeSession.status || "confirmed",
                actioned_by: cancelParams.cancellerId || "",
                action: "cancelled",
                actioned_on: new Date(),
                practice_id: intakeSession.practice_id || "",
                clinician_display_name: intakeSession.clinician_display_name || "",
                service_delivery_option:
                    intakeSession.service_delivery_option || "remote",
                location: intakeSession.location || "Remote",
                recurrence: intakeSession.recurrence || RecurrenceRule.ONE_OFF,
                reason: "The supervisee's appointment this session was booked for was cancelled",
                booking_requested_service: appt.requested_service || "",
                booking_clinician_display_name: appt.clinician_display_name || "",
                booking_start: appt.start || "",
                booking_starting_date: appt.starting_date || "",
                booking_practice_id: appt.practice_id || "",
            };

            updatedClientChangesToSave.intake_session_history!.push(
                intakeSessionHistoryEntry
            );

            // Track supervisor intake session that needs to be removed
            supervisorIntakeSessionsToCancel.push({
                supervisorId: intakeSession.supervisor_id,
                bookingId: appt.id,
                clientId: appt.client_id,
            });
        }

        // Remove the appointment from client and clinician.
        updatedClientChangesToSave.confirmed_bookings =
            updatedClient.confirmed_bookings.filter((b) => b.id !== appt.id);
        for (const clincianId in updatedClininicianChangesToSave) {
            const confirmed = updatedClininicianChangesToSave[clincianId].confirmed;
            updatedClininicianChangesToSave[clincianId].confirmed = confirmed.filter(
                (b) => b.id !== appt.id
            );
        }
    }

    return {
        canCancel: true,
        updatedClientChangesToSave: updatedClientChangesToSave,
        updatedClininicianChangesToSave: updatedClininicianChangesToSave,
        supervisorIntakeSessionsToCancel:
            supervisorIntakeSessionsToCancel.length > 0
                ? supervisorIntakeSessionsToCancel
                : [],
        numberOfCancellations: appointmentsToCancel.length,
    };
}
